/**
 * Sawyer answers 10-29, item 2 — "Going home today", the list.
 *
 * Sawyer: the desk uses Gingr's Check Out Report as the list of dogs going
 * home that day, "so belongings and their food go home with them". Pure: no
 * React, no I/O, so the screen list and the printed sheet are the same list
 * (Principle 1) and the rules are unit-tested.
 *
 * WHO IS ON IT. Every visit the server marks `checksOutToday` (the
 * departures, plus expected arrivals whose stay ends today: a daycare dog
 * arriving this morning goes home tonight). A service leg is never its own
 * dog: legs are folded onto their visit first (`foldServiceLegs`), and an
 * orphan leg (its visit not on today's feed) is left off, because the dog it
 * belongs to is not leaving today.
 *
 * DEPLOY SKEW. The frontend auto-deploys on merge and the server does not.
 * A server older than this lane sends no `checksOutToday` and no `goingHome`:
 * the list is then the checked-in departures (exactly the existing Going home
 * list), `mode: 'checked_in_only'`, and the rows carry `care: 'not_sent'` so
 * the belongings / locker / food columns are not drawn at all. Absent is
 * never rendered as "none".
 *
 * GROUPING. One row per DOG. A household (same customer id, never the same
 * name) stays together under its owner; it sits in the time block of its
 * earliest pick-up. Blocks are whole hours in clock order; dogs with no
 * pick-up time set get their own block at the end, said honestly.
 *
 * CHECK OUT (Sawyer, 10-30: "I didn't see a check out button on the going home
 * section"). Check-out is per VISIT, so a checked-in visit offers it on its
 * first dog, and a second dog on that visit leaves with the first, as on the
 * Going home board, which has one Check out per visit. An expected arrival has
 * nothing to check out of yet. A dog already checked out is not on `/today`, so
 * it has left this list; the `left` state keeps the button off if a row like
 * that ever does arrive here.
 */
import type {
  DashboardArrival,
  GoingHomeFood,
} from '../dashboard/dashboard-api.js';
import { foldServiceLegs, serviceRowLabel } from '../dashboard/service-legs.js';
import { formatClock } from '../dashboard/day-list-filters.js';

export type GoingHomeCareState = 'present' | 'not_loaded' | 'not_sent';

/** What a dog's row says at check-out. Only `ready` draws a button. */
export type GoingHomeCheckOut =
  /** Checked in: this row checks out the visit, which is these dogs. */
  | { kind: 'ready'; dogNames: string[] }
  /** Checked in on the same visit as `leadDogName`, whose row has the button. */
  | { kind: 'with_visit'; leadDogName: string }
  /** Expected today and not here yet. */
  | { kind: 'not_checked_in' }
  /** On the departures list but no longer checked in. */
  | { kind: 'left' };

export interface GoingHomeDogRow {
  /** Unique per (visit, dog). */
  key: string;
  bookingId: string;
  dogId: string | null;
  dogName: string;
  customerId: string | null;
  customerName: string;
  serviceLabel: string;
  /** Added services on the visit ("Nail Trim"), so the desk knows they ride along. */
  addedServices: string[];
  /** HH:MM or null. */
  pickUpTime: string | null;
  /** The dog has not been checked in yet (an expected arrival). */
  notCheckedIn: boolean;
  checkOut: GoingHomeCheckOut;
  /** present = the columns below are real; not_loaded = the server could not read them; not_sent = older server. */
  care: GoingHomeCareState;
  belongings: string[];
  locker: string | null;
  food: GoingHomeFood | null;
}

export interface GoingHomeHousehold {
  key: string;
  customerId: string | null;
  customerName: string;
  rows: GoingHomeDogRow[];
}

export interface GoingHomeBlock {
  key: string;
  label: string;
  households: GoingHomeHousehold[];
}

export interface GoingHomeList {
  blocks: GoingHomeBlock[];
  dogCount: number;
  /** scheduled = every visit checking out today; checked_in_only = an older server (departures only). */
  mode: 'scheduled' | 'checked_in_only';
  /** False when no row carries the per-dog facts (older server): draw no belongings / locker / food columns. */
  careSent: boolean;
  /** Some dog's food could not be told apart because House Food is not set up as a meal charge. */
  houseFoodNotRecorded: boolean;
}

const NO_TIME = 'none';

function minutesOf(hhmm: string | null): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

function hourLabel(h: number): string {
  const suffix = h >= 12 && h < 24 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${suffix}`;
}

/** "4 PM – 5 PM" for 16:xx; the no-time block says so. */
export function blockLabel(key: string): string {
  if (key === NO_TIME) return 'No pick-up time set';
  const h = Number(key.slice(1));
  return `${hourLabel(h)} – ${hourLabel((h + 1) % 24)}`;
}

/** "16:30" → "4:30 PM". */
export function pickUpLabel(hhmm: string | null): string | null {
  return formatClock(hhmm);
}

function hasKey<K extends string>(row: object, key: K): boolean {
  return Object.prototype.hasOwnProperty.call(row, key);
}

/** A leg's dog ids, or null when the leg names none (it then carries every dog on the visit). */
function legDogIds(leg: DashboardArrival): Set<string> | null {
  const ids = (Array.isArray(leg.dogs) ? leg.dogs : []).map((d) => d.dogId);
  return ids.length > 0 ? new Set(ids) : null;
}

function dogRowsOf(
  row: DashboardArrival,
  legs: readonly DashboardArrival[],
  notCheckedIn: boolean,
): GoingHomeDogRow[] {
  const careSent = hasKey(row, 'goingHome');
  const care = row.goingHome ?? null;
  const pickUpTime = (careSent ? care?.pickUpTime : undefined) ?? row.pickUpTime ?? null;
  const dogs =
    Array.isArray(row.dogs) && row.dogs.length > 0
      ? row.dogs.map((d) => ({ dogId: d.dogId as string | null, name: d.name }))
      : row.dogNames.map((name) => ({ dogId: null as string | null, name }));
  const lead = dogs[0]?.name ?? '';
  const checkOutOf = (i: number): GoingHomeCheckOut => {
    if (notCheckedIn) return { kind: 'not_checked_in' };
    if (row.status !== 'checked_in') return { kind: 'left' };
    return i === 0 ? { kind: 'ready', dogNames: dogs.map((x) => x.name) } : { kind: 'with_visit', leadDogName: lead };
  };
  return dogs.map((d, i) => {
    const facts = d.dogId && care ? care.dogs.find((c) => c.dogId === d.dogId) : undefined;
    const state: GoingHomeCareState = !careSent ? 'not_sent' : facts ? 'present' : 'not_loaded';
    // An added service names the dog it is for: Hank's nail trim is on
    // Hank's row, not on his sister's.
    const addedServices = legs
      .filter((leg) => {
        const ids = legDogIds(leg);
        return ids === null || d.dogId === null || ids.has(d.dogId);
      })
      .map(serviceRowLabel)
      .filter((l) => l.length > 0);
    return {
      key: `${row.bookingId}:${d.dogId ?? i}`,
      bookingId: row.bookingId,
      dogId: d.dogId,
      dogName: d.name,
      customerId:
        typeof row.customerId === 'string' && row.customerId.trim().length > 0 ? row.customerId : null,
      customerName: row.customerName,
      serviceLabel: serviceRowLabel(row),
      addedServices,
      pickUpTime,
      notCheckedIn,
      checkOut: checkOutOf(i),
      care: state,
      belongings: facts?.belongings ?? [],
      locker: facts?.locker ?? null,
      food: facts?.food ?? null,
    };
  });
}

function compareRows(a: GoingHomeDogRow, b: GoingHomeDogRow): number {
  const ma = minutesOf(a.pickUpTime);
  const mb = minutesOf(b.pickUpTime);
  if (ma !== mb) {
    if (ma === null) return 1;
    if (mb === null) return -1;
    return ma - mb;
  }
  return 0;
}

export function buildGoingHomeList(input: {
  arrivals: readonly DashboardArrival[];
  departures: readonly DashboardArrival[];
}): GoingHomeList {
  const all = [...input.departures, ...input.arrivals];
  const serverMarks = all.some((r) => typeof r.checksOutToday === 'boolean');
  const mode: GoingHomeList['mode'] = serverMarks || all.length === 0 ? 'scheduled' : 'checked_in_only';

  const rows: GoingHomeDogRow[] = [];
  const take = (list: readonly DashboardArrival[], notCheckedIn: boolean, needsMark: boolean): void => {
    for (const entry of foldServiceLegs(list)) {
      if (entry.orphanLeg || entry.row.isLeg === true) continue;
      if (needsMark && entry.row.checksOutToday !== true) continue;
      rows.push(...dogRowsOf(entry.row, entry.legs, notCheckedIn));
    }
  };
  if (serverMarks) {
    take(input.departures, false, true);
    take(input.arrivals, true, true);
  } else {
    take(input.departures, false, false);
  }

  // Households: by customer id only (two "J. Smith"s are not one family).
  const households: GoingHomeHousehold[] = [];
  const byCustomer = new Map<string, GoingHomeHousehold>();
  for (const row of rows) {
    if (row.customerId === null) {
      households.push({ key: `row:${row.key}`, customerId: null, customerName: row.customerName, rows: [row] });
      continue;
    }
    const existing = byCustomer.get(row.customerId);
    if (existing) {
      existing.rows.push(row);
      continue;
    }
    const h: GoingHomeHousehold = {
      key: `customer:${row.customerId}`,
      customerId: row.customerId,
      customerName: row.customerName,
      rows: [row],
    };
    byCustomer.set(row.customerId, h);
    households.push(h);
  }
  for (const h of households) h.rows.sort(compareRows);

  const blocks = new Map<string, GoingHomeHousehold[]>();
  for (const h of households) {
    const minutes = minutesOf(h.rows[0]?.pickUpTime ?? null);
    const key = minutes === null ? NO_TIME : `h${Math.floor(minutes / 60)}`;
    const list = blocks.get(key);
    if (list) list.push(h);
    else blocks.set(key, [h]);
  }
  const ordered = [...blocks.entries()]
    .sort(([a], [b]) => {
      if (a === NO_TIME) return 1;
      if (b === NO_TIME) return -1;
      return Number(a.slice(1)) - Number(b.slice(1));
    })
    .map(([key, hs]) => ({
      key,
      label: blockLabel(key),
      households: hs.sort(
        (x, y) =>
          compareRows(x.rows[0]!, y.rows[0]!) ||
          x.customerName.localeCompare(y.customerName),
      ),
    }));

  return {
    blocks: ordered,
    dogCount: rows.length,
    mode,
    careSent: rows.some((r) => r.care !== 'not_sent'),
    houseFoodNotRecorded: rows.some((r) => r.food?.reason === 'house_food_not_recorded'),
  };
}

/** The food words a row shows. null = nothing to say (no meals here). */
export function foodLabel(row: Pick<GoingHomeDogRow, 'care' | 'food'>): {
  text: string;
  tone: 'send' | 'house' | 'check' | 'unknown';
} | null {
  if (row.care === 'not_sent') return null;
  if (row.care === 'not_loaded' || row.food === null) return { text: "Food: couldn't load", tone: 'unknown' };
  const f = row.food;
  switch (f.status) {
    case 'send_home':
      if (f.reason === 'in_belongings') return { text: `Send food home: ${f.foodItems.join(', ')}`, tone: 'send' };
      if (f.reason === 'part_house_food') return { text: 'Send food home (House Food some days)', tone: 'send' };
      return { text: 'Send food home', tone: 'send' };
    case 'house_food':
      return { text: 'House food', tone: 'house' };
    case 'unknown':
      return { text: 'Check for food', tone: 'check' };
    case 'no_meals':
      return null;
  }
}

/** The belongings words a row shows. null = the column is not drawn (older server). */
export function belongingsLabel(row: Pick<GoingHomeDogRow, 'care' | 'belongings'>): string | null {
  if (row.care === 'not_sent') return null;
  if (row.care === 'not_loaded') return "Couldn't load";
  return row.belongings.length > 0 ? row.belongings.join(', ') : 'No belongings listed';
}
